import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, ShieldCheck, Sparkles, Trophy, MapPin, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { LeadingFor } from "@/components/LeadingFor";

const CFG = {
  1: { ped: "h-40 sm:h-56", num: "text-6xl sm:text-8xl", tint: "from-amber-400/40",
    card: "glass-strong p-4 sm:p-7 border-amber-400/50 bg-gradient-to-b from-amber-500/[0.08] to-transparent shadow-[0_0_40px_-8px_rgba(251,191,36,0.45)]", avatar: "w-14 h-14 sm:w-24 sm:h-24",
    name: "text-base sm:text-2xl", bid: "text-2xl sm:text-4xl text-amber-300" },
  2: { ped: "h-24 sm:h-36", num: "text-4xl sm:text-6xl", tint: "from-blue-400/25",
    card: "glass-strong p-3 sm:p-5", avatar: "w-10 h-10 sm:w-16 sm:h-16",
    name: "text-sm sm:text-lg", bid: "text-lg sm:text-2xl text-white" },
  3: { ped: "h-16 sm:h-24", num: "text-4xl sm:text-6xl", tint: "from-violet-400/25",
    card: "glass-strong p-3 sm:p-5", avatar: "w-10 h-10 sm:w-16 sm:h-16",
    name: "text-sm sm:text-lg", bid: "text-lg sm:text-2xl text-white" },
};

function focusSearch() {
  const el = document.querySelector('[data-testid="my-hero-search-input"]');
  if (el) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => el.focus(), 400);
  }
}

function PodiumColumn({ place, entry, delay }) {
  const navigate = useNavigate();
  const c = CFG[place];
  const isFirst = place === 1;

  const card = entry ? (
    <button
      data-testid={`my-podium-${place}`}
      onClick={() => navigate(`/my/k/${entry.slug}`)}
      className={`w-full text-left ${c.card} rounded-2xl sm:rounded-3xl hover:-translate-y-1.5 hover:border-white/30 transition-[transform,border-color]`}
    >
      <div className="flex flex-col items-center text-center min-w-0">
        {isFirst && <Crown className="w-6 h-6 sm:w-9 sm:h-9 text-amber-300 drop-shadow-[0_0_10px_rgba(251,191,36,0.8)] mb-2 sm:mb-3" />}
        <div className={`${c.avatar} rounded-xl sm:rounded-2xl overflow-hidden glass flex items-center justify-center shrink-0 font-display font-bold text-lg`}>
          {entry.logo_url ? <img src={entry.logo_url} alt="" className="w-full h-full object-cover" /> : (entry.business_name || "?")[0]}
        </div>
        <div className="mt-2 sm:mt-3 w-full min-w-0">
          <div className="flex items-center justify-center gap-1">
            <h3 className={`font-display font-bold tracking-tight leading-tight break-normal line-clamp-2 ${c.name}`}>{entry.business_name}</h3>
            {entry.is_brand_lock && <ShieldCheck className="w-4 h-4 text-yellow-400 shrink-0" />}
          </div>
          <div className={`font-display font-black tabular-nums leading-none mt-1 ${c.bid}`}>
            RM {Number(entry.max_daily_cap).toFixed(2)}
            <span className="text-[10px] sm:text-xs text-white/50 font-sans font-normal ml-0.5">locked</span>
          </div>
          <div className="inline-flex mt-1.5 sm:mt-2 max-w-full">
            <span className="text-[10px] sm:text-xs text-blue-300/90 bg-blue-500/10 border border-blue-500/20 rounded-full px-2 py-0.5 truncate">
              /my/{entry.slug}
            </span>
          </div>
          {(entry.google_maps_url || entry.phone_number) && (
            <div className="flex items-center justify-center gap-3 mt-1.5">
              {entry.google_maps_url && (
                <a
                  data-testid="my-podium-maps-link"
                  href={entry.google_maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  title="Open in Google Maps"
                  className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-white/45 hover:text-blue-300 transition-colors"
                >
                  <MapPin className="w-3.5 h-3.5" /> Map
                </a>
              )}
              {entry.phone_number && (
                <a
                  data-testid="my-podium-phone-link"
                  href={`tel:${String(entry.phone_number).replace(/[^0-9+]/g, "")}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-white/45 hover:text-emerald-300 transition-colors"
                >
                  <Phone className="w-3.5 h-3.5" /> Call
                </a>
              )}
            </div>
          )}
          {isFirst && entry.held_since && (
            <div className="mt-1.5"><LeadingFor since={entry.held_since} className="text-[10px] sm:text-xs" /></div>
          )}
        </div>
      </div>
    </button>
  ) : (
    <button
      data-testid={`my-podium-empty-${place}`}
      onClick={focusSearch}
      className="w-full glass rounded-2xl sm:rounded-3xl border-2 border-dashed border-white/20 p-4 sm:p-7 text-center hover:border-blue-500/50 hover:bg-blue-500/5 transition-colors cta-pulse flex flex-col items-center justify-center"
    >
      <Sparkles className="w-6 h-6 sm:w-9 sm:h-9 text-blue-400 mb-2" />
      <div className="text-xs sm:text-base font-semibold text-white/85">Claim this spot</div>
      <div className="text-[10px] sm:text-xs text-white/40 mt-0.5">from RM 5</div>
    </button>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 260, damping: 24 }}
      className="flex flex-col justify-end"
    >
      {card}
      <div className={`relative mt-3 sm:mt-4 ${c.ped} rounded-t-xl sm:rounded-t-2xl glass-strong border-x border-t border-white/10 flex items-start justify-center pt-2 sm:pt-4 overflow-hidden`}>
        <div className={`absolute inset-0 bg-gradient-to-t ${c.tint} to-transparent`} />
        <span className={`relative font-display font-black text-white/25 ${c.num} leading-none`}>{place}</span>
      </div>
    </motion.div>
  );
}

export function MyPodium() {
  const [top, setTop] = useState([]);

  useEffect(() => {
    api.get("/global-top3", { params: { region: "MY" } }).then(({ data }) => setTop(data.top)).catch(() => {});
  }, []);

  return (
    <section className="max-w-6xl mx-auto px-4 pt-4 pb-16 sm:pb-20" data-testid="my-podium">
      <div className="flex items-center justify-center gap-2 mb-1">
        <Trophy className="w-5 h-5 text-emerald-400" />
        <h2 className="font-display font-bold text-xl sm:text-3xl tracking-tight text-center">The Podium 🇲🇾</h2>
      </div>
      <p className="text-center text-white/50 text-sm sm:text-base mb-8 sm:mb-12">The highest RM bids dominating the Malaysian boards — right now.</p>

      <div className="relative grid grid-cols-3 gap-3 sm:gap-8 items-end">
        <div className="absolute left-1/2 -translate-x-1/2 top-4 w-[60%] h-[85%] bg-emerald-500/25 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute left-1/2 -translate-x-1/2 top-10 w-[30%] h-[60%] bg-emerald-400/20 rounded-full blur-[70px] pointer-events-none" />
        <PodiumColumn place={2} entry={top[1] || null} delay={0.18} />
        <PodiumColumn place={1} entry={top[0] || null} delay={0} />
        <PodiumColumn place={3} entry={top[2] || null} delay={0.28} />
      </div>
    </section>
  );
}
